import React, { useState, useEffect, useContext, useRef } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import "./walkinModal.css"; // you can just reuse bookingModal.css here if you want
import { CombinedContext } from "../../../context/combinedContext";
import companyBasedRequest from "../../../request/companyBasedRequest";
import CustomSelect from "../../common/customSelect/customSelect";
import translations from "../../../translations/translations";
import { ChevronLeft, ChevronRight } from "lucide-react";

dayjs.extend(utc);
dayjs.extend(timezone);

const TZ = "America/Santo_Domingo";

const WalkinModal = ({ onClose, staffList = [], token, week, services: shopServices = [], selectedDate }) => {
	const { language, userData } = useContext(CombinedContext);

	const role = userData.user.role;

	const [selectedStaffId, setSelectedStaffId] = useState(staffList[0]?.staffId || null);

	const initialDate = week?.currentDay || week?.weekStart || dayjs().tz(TZ).format("YYYY-MM-DD");

	const selectedStaff = React.useMemo(() => staffList.find((s) => s.staffId === selectedStaffId) || staffList[0] || null, [staffList, selectedStaffId]);

	const [currentDate, setCurrentDate] = useState(selectedDate);
	const [timePeriod, setTimePeriod] = useState("Morning");
	const [selectedTime, setSelectedTime] = useState("");
	const [selectedServiceId, setSelectedServiceId] = useState("");
	const [manualDuration, setManualDuration] = useState(null);
	const [editDuration, setEditDuration] = useState(false);
	const [customerName, setCustomerName] = useState("");
	const [saving, setSaving] = useState(false);
	const [toast, setToast] = useState("");
	const [validationError, setValidationError] = useState("");
	const [startDate, setStartDate] = useState(selectedDate);
	const [dayIndex, setDayIndex] = useState(0);
	const [selTime, setSelTime] = useState("");
	const [date, setDate] = useState(dayjs().tz("America/Santo_Domingo").format("YYYY-MM-DD"));
	const [initialized, setInitialized] = useState(false);
	const slotsRef = useRef(null);
	const [canScrollLeft, setCanScrollLeft] = useState(false);
	const [canScrollRight, setCanScrollRight] = useState(true);

	const daysRef = useRef(null);

	useEffect(() => {
		if (!daysRef.current || !currentDate) return;

		const buttons = Array.from(daysRef.current.querySelectorAll(".sf-book-day"));

		const index = buttons.findIndex((_, i) => Object.keys(selectedStaff.week || {})[i] === currentDate);

		if (index === -1) return;

		const btn = buttons[index];

		// Center if possible, otherwise start/end naturally
		btn.scrollIntoView({
			behavior: "smooth",
			inline: "center",
			block: "nearest",
		});
	}, [currentDate, selectedStaff]);

	useEffect(() => {
		const prev = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = prev || "";
		};
	}, []);

	useEffect(() => {
		if (staffList.length && !selectedStaffId) {
			setSelectedStaffId(staffList[0].staffId);
		}
	}, [staffList]);

	useEffect(() => {
		if (!initialized) {
			setInitialized(true);
			setCurrentDate(selectedDate);
		} else if (week) {
			const d = week.currentDay || week.weekStart || dayjs().tz(TZ).format("YYYY-MM-DD");
			setCurrentDate(d);
		}
	}, [week]);

	const datesInWeek = React.useMemo(() => {
		if (!week?.weekStart) return [];
		const start = dayjs(week.weekStart);
		return Array.from({ length: 7 }, (_, i) => start.add(i, "day").format("YYYY-MM-DD"));
	}, [week]);

	const dayData = React.useMemo(() => {
		if (!selectedStaff || !selectedStaff.week) return null;
		return selectedStaff.week[currentDate] || null;
	}, [selectedStaff, currentDate]);

	const staffServiceIds = selectedStaff?.services || [];

	const services = React.useMemo(() => {
		// Must always filter based on the staff's allowed service IDs
		if (!selectedStaff) return [];

		const ids = selectedStaff.services || [];

		// Filter shop services by those IDs
		const allowed = (shopServices || []).filter((svc) => ids.includes(svc.serviceId));

		return allowed;
	}, [selectedStaff, shopServices]);

	const selectedService = React.useMemo(() => services.find((s) => String(s.serviceId) === String(selectedServiceId)), [services, selectedServiceId]);

	const serviceOptions = services.map((s) => ({
		value: s.serviceId,
		label: (s.nameEn || s.name || s.serviceNameEn || s.serviceNameEs) + (s.duration ? ` • ${s.duration} min` : ""),
	}));

	function parseTimeToMinutes(t) {
		if (!t) return null;
		if (t.includes("T")) {
			const d = dayjs(t);
			return d.hour() * 60 + d.minute();
		}
		const [h, m] = t.split(":").map(Number);
		return h * 60 + (m || 0);
	}

	function getTotalDurationMinutes() {
		if (editDuration && manualDuration && manualDuration > 0) {
			return manualDuration;
		}
		if (selectedService && selectedService.duration) {
			return selectedService.duration;
		}
		// default 30
		return 30;
	}

	// Build intervals (in minutes from midnight) for a day
	function buildIntervals(dayData) {
		if (!dayData) {
			return {
				shifts: [],
				breaks: [],
				bookings: [],
				blocks: [],
			};
		}

		const toInterval = (start, end) => {
			const s = parseTimeToMinutes(start);
			const e = parseTimeToMinutes(end);
			if (s === null || e === null) return null;
			return { start: s, end: e };
		};

		const shifts = (dayData.shifts || []).map((s) => toInterval(s.start, s.end)).filter(Boolean);

		const breaks = (dayData.breaks || []).map((b) => toInterval(b.start, b.end)).filter(Boolean);

		const bookings = (dayData.bookings || []).map((b) => toInterval(b.start, b.end)).filter(Boolean);

		const blocks = (dayData.blocks || []).map((b) => toInterval(b.start, b.end)).filter(Boolean);

		return { shifts, breaks, bookings, blocks };
	}

	function overlaps(a, b) {
		// intervals in minutes: {start, end}
		return a.end > b.start && a.start < b.end;
	}

	function isInsideAny(interval, intervals) {
		return intervals.some((sh) => interval.start >= sh.start && interval.end <= sh.end);
	}

	function isFreeFrom(interval, intervals) {
		return intervals.every((block) => !overlaps(interval, block));
	}

	// Build all valid slot start times as "HH:mm"
	function buildSlotsForDay(dayData) {
		if (!dayData) return [];
		const { shifts, breaks, bookings, blocks } = buildIntervals(dayData);

		if (!shifts.length) return [];

		const totalDur = getTotalDurationMinutes();

		const slots = [];
		shifts.forEach((shift) => {
			// last possible start time so that [start, start + totalDur] fits in shift
			for (
				let start = shift.start;
				start + totalDur <= shift.end;
				start += 15 // 15-min increments
			) {
				const candidate = { start, end: start + totalDur };

				// Must be fully inside at least one shift (already in this shift loop but keep generic)
				if (!isInsideAny(candidate, shifts)) continue;

				// Must not overlap breaks, bookings, blocks
				if (!isFreeFrom(candidate, breaks)) continue;
				if (!isFreeFrom(candidate, bookings)) continue;
				if (!isFreeFrom(candidate, blocks)) continue;

				slots.push(start);
			}
		});

		// Deduplicate & sort
		const unique = Array.from(new Set(slots)).sort((a, b) => a - b);

		return unique.map((mins) => {
			const h = String(Math.floor(mins / 60)).padStart(2, "0");
			const m = String(mins % 60).padStart(2, "0");
			return `${h}:${m}`;
		});
	}

	function getTimePeriodFromHour(hour) {
		if (hour < 12) return "Morning";
		if (hour < 17) return "Afternoon";
		return "Evening";
	}

	const allSlots = React.useMemo(() => buildSlotsForDay(dayData), [dayData, selectedService, manualDuration, editDuration]);

	const availablePeriods = React.useMemo(() => {
		const set = new Set();
		allSlots.forEach((t) => {
			const [hStr] = t.split(":");
			const h = parseInt(hStr, 10);
			set.add(getTimePeriodFromHour(h));
		});
		return set;
	}, [allSlots]);

	function staffHasSlots(staff, dateStr) {
		const d = staff.week?.[dateStr];
		if (!d) return false;

		const slots = buildSlotsForDay(d);
		return slots.length > 0;
	}

	function anyStaffHasSlotsForDay(dateStr) {
		return staffList.some((s) => staffHasSlots(s, dateStr));
	}

	async function handleConfirm() {
		setValidationError("");

		if (!selectedStaff) {
			setValidationError(translations[language].calendar.walkin.validation.selectStaff);
			return;
		}
		if (!selectedService) {
			setValidationError(translations[language].calendar.walkin.validation.selectService);
			return;
		}
		if (!selectedTime) {
			setValidationError(translations[language].calendar.walkin.validation.selectTime);
			return;
		}

		const totalMinutes = getTotalDurationMinutes();
		const startLocal = dayjs.tz(`${currentDate}T${selectedTime}`, TZ);
		const endLocal = startLocal.add(totalMinutes, "minute");

		const now = dayjs().tz(TZ);
		if (startLocal.isBefore(now)) {
			setValidationError(translations[language].calendar.walkin.validation.pastTime);
			return;
		}

		const isStaff = role === "staff";

		const payload = {
			shopId: isStaff ? userData.user.shopId : week?.shopId || selectedStaff?.shopId,

			// shopName: isStaff ? userData.shop.shopName : week?.shopName,

			staffId: isStaff ? userData.user.userId : selectedStaff?.staffId,

			startTime: startLocal.format("YYYY-MM-DDTHH:mm:ss"),
			endTime: endLocal.format("YYYY-MM-DDTHH:mm:ss"),

			customerName: customerName?.trim() || "Walk-In",

			serviceId: selectedService.serviceId,
			serviceNameEn: selectedService.serviceNameEn || selectedService.nameEn || selectedService.name || selectedService.serviceNameEs,

			serviceNameEs: selectedService.serviceNameEs || selectedService.nameEs || selectedService.name || selectedService.serviceNameEn,

			amount: typeof selectedService.price === "number" ? selectedService.price : 0,
		};

		try {
			setSaving(true);
			await companyBasedRequest.manualBooking(payload, token);
			setToast(translations[language].calendar.walkin.toast.success);
			setTimeout(() => {
				setToast("");
				onClose?.();
			}, 800);
		} catch (err) {
			console.error("Failed to create walk-in:", err);
			setValidationError(translations[language].calendar.walkin.toast.error);
		} finally {
			setSaving(false);
		}
	}

	function parseLocalDate(str) {
		const [y, m, d] = str.split("-").map(Number);
		return dayjs.tz(`${str} 12:00`, "America/Santo_Domingo").toDate();
	}

	function getCombinedBarStatus(dateStr) {
		let anyShifts = false;
		let totalSlots = 0;

		for (const staff of staffList) {
			const d = staff.week?.[dateStr];
			if (!d) continue;

			if (d.shifts.length > 0) anyShifts = true;

			const slots = buildSlotsForDay(d);
			totalSlots += slots.length;
		}

		if (!anyShifts) return "closed";
		if (totalSlots === 0) return "full";
		if (totalSlots < 6) return "busy";
		return "open";
	}

	function getFirstAvailableStaff(dateStr) {
		for (const staff of staffList) {
			if (!staff.week?.[dateStr]) continue;
			const slots = buildSlotsForDay(staff.week[dateStr]);
			if (slots.length > 0) return staff; // first staff with real slots
		}
		return null;
	}

	if (!week || !staffList.length) return null;

	return (
		<div className="sf-book-overlay" onClick={onClose}>
			<div className="sf-book-modal" onClick={(e) => e.stopPropagation()}>
				{/* HEADER */}
				<div className="sf-book-weeknav">
					<span className="sf-book-weeklabel">{translations[language].calendar.walkin.title}</span>
					<button className="sf-book-close" onClick={onClose}>
						✕
					</button>
				</div>

				{/* BODY */}
				<div className="sf-book-body">
					{/* DAYS STRIP (7 days from weekStart) */}
					<div className="sf-book-days-with-arrows">

						<div className="sf-book-days" ref={daysRef}>
							{Object.entries(selectedStaff.week || {}).map(([dateStr, d]) => {
								const isSel = dateStr === currentDate;

								return (
									<button
										key={dateStr}
										disabled={!anyStaffHasSlotsForDay(dateStr)}
										className={`sf-book-day ${isSel ? "is-active" : ""}`}
										onClick={() => {
											if (!anyStaffHasSlotsForDay(dateStr)) return;

											setCurrentDate(dateStr);
											setSelectedTime("");

											if (!staffHasSlots(selectedStaff, dateStr)) {
												const available = getFirstAvailableStaff(dateStr);
												if (available) {
													setSelectedStaffId(available.staffId);
													setSelectedServiceId("");
												}
											}
										}}
									>
										<div className="sf-book-daytop">
											<span className="sf-day-num">{parseLocalDate(dateStr).getDate()}</span>
										</div>

										<div className={`sf-book-daybar ${getCombinedBarStatus(dateStr)}`} />
									</button>
								);
							})}
						</div>

					</div>

					{/* TIME FILTERS (Morning / Afternoon / Evening) */}
					<div className="sf-time-filters">
						{["Morning", "Afternoon", "Evening"].map((label, key) => {
							const isAvailable = availablePeriods.has(label);

							return (
								<button
									key={label}
									className={`sf-filter-segment ${timePeriod === label ? "active" : ""} ${!isAvailable ? "disabled" : ""}`}
									onClick={() => {
										if (!isAvailable) return;
										setTimePeriod(label);
										if (!slotsRef.current) return;

										// scroll roughly to first slot in that period
										const ranges = { Morning: 0, Afternoon: 12, Evening: 17 };
										const targetBtn = Array.from(slotsRef.current.querySelectorAll(".sf-book-slot")).find((btn) => {
											const [hourStr] = btn.textContent.trim().split(":");
											const hour = parseInt(hourStr, 10);
											return hour >= ranges[label];
										});

										if (targetBtn) {
											targetBtn.scrollIntoView({
												behavior: "smooth",
												inline: "center",
												block: "nearest",
											});
										}
									}}
									disabled={!isAvailable}
								>
									{translations[language].calendar.walkin.timePeriods[key]}
								</button>
							);
						})}
					</div>

					{/* TIME SLOTS SCROLL STRIP */}
					<div className="sf-time-scroll">
						<button
							className={`sf-scroll-btn ${!canScrollLeft ? "disabled" : ""}`}
							onClick={() => {
								if (slotsRef.current) slotsRef.current.scrollBy({ left: -300, behavior: "smooth" });
							}}
						>
							<ChevronLeft />
						</button>

						<div
							className="sf-time-container"
							ref={slotsRef}
							onScroll={() => {
								if (!slotsRef.current) return;
								const { scrollLeft, scrollWidth, clientWidth } = slotsRef.current;
								setCanScrollLeft(scrollLeft > 0);
								setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
							}}
						>
							{allSlots.length > 0 ? (
								allSlots.map((t) => (
									<button key={t} className={`sf-book-slot ${selectedTime === t ? "is-selected" : ""}`} onClick={() => setSelectedTime(t)}>
										{t}
									</button>
								))
							) : (
								<div className="sf-noslots">{translations[language].calendar.walkin.noSlots}</div>
							)}
						</div>

						<button
							className={`sf-scroll-btn ${!canScrollRight ? "disabled" : ""}`}
							onClick={() => {
								if (slotsRef.current) slotsRef.current.scrollBy({ left: 300, behavior: "smooth" });
							}}
						>
							<ChevronRight />
						</button>
					</div>

					{/* STAFF SELECT */}
					{(role === "owner" || role === "admin") && (
						<div className="sf-box">
							<h3>{translations[language].calendar.walkin.staff.select}</h3>

							<div className="sf-staff-pills">
								{staffList
									.filter((s) => staffHasSlots(s, currentDate))
									.map((s) => (
										<div
											key={s.staffId}
											className={`sf-staff-pill ${selectedStaffId === s.staffId ? "active" : ""}`}
											onClick={() => {
												setSelectedStaffId(s.staffId);
												setSelectedTime("");
												// setSelectedScheduleId(s.scheduleId);
												// loadSchedule(s);
											}}
										>
											<div className="sf-staff-avatar">
												{s.photo ? <img src={s.photo} alt={s.firstName} /> : <span>{s.firstName?.charAt(0)}</span>}
											</div>

											<span className="sf-staff-name">
												{s.firstName} {s.lastName}
											</span>
										</div>
									))}
							</div>
						</div>
					)}

					{/* SERVICES SELECT */}
					<div className="sf-box">
						<h3>{translations[language].calendar.walkin.service.label}</h3>

						{services.length === 0 ? (
							<div style={{ fontSize: 14, color: "#777" }}>{translations[language].calendar.walkin.service.none}</div>
						) : (
							<CustomSelect
								value={selectedServiceId}
								onChange={(val) => {
									setSelectedServiceId(val);
									setEditDuration(false);
									setManualDuration(null);
								}}
								placeholder={translations[language].calendar.walkin.service.placeholder}
								options={serviceOptions}
								// className="sf-book-input"
							/>
						)}
					</div>

					{/* DURATION (Booksy style override) */}
					<div className="sf-box">
						<h3>{translations[language].calendar.walkin.duration.label}</h3>
						{!editDuration ? (
							<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
								<span>{getTotalDurationMinutes()} min</span>
								<button
									type="button"
									style={{
										border: "none",
										background: "transparent",
										color: "#007bff",
										cursor: "pointer",
										fontSize: 14,
									}}
									onClick={() => {
										setEditDuration(true);
										setManualDuration(getTotalDurationMinutes());
									}}
								>
									{translations[language].calendar.walkin.duration.edit}
								</button>
							</div>
						) : (
							<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
								<input
									type="number"
									min="5"
									step="5"
									className="sf-book-input"
									value={manualDuration ?? ""}
									onChange={(e) => setManualDuration(Number(e.target.value) || 0)}
									style={{ maxWidth: 120 }}
								/>
								<span>min</span>
								<button
									type="button"
									style={{
										border: "none",
										background: "transparent",
										color: "#007bff",
										cursor: "pointer",
										fontSize: 14,
									}}
									onClick={() => {
										setEditDuration(false);
										setManualDuration(null);
									}}
								>
									{translations[language].calendar.walkin.duration.done}
								</button>
							</div>
						)}
					</div>

					{/* CLIENT NAME (optional) */}
					<div className="sf-box">
						<h3>{translations[language].calendar.walkin.client.label}</h3>
						<input
							className="sf-book-input"
							type="text"
							placeholder={translations[language].calendar.walkin.client.placeholder}
							value={customerName}
							onChange={(e) => setCustomerName(e.target.value)}
						/>
					</div>
				</div>

				{/* VALIDATION ERROR */}
				{validationError && <div className="sf-book-errorbar">{validationError}</div>}

				{/* FOOTER */}
				<div className="sf-book-footer">
					<div className="sf-footer-summary">
						<span>
							{selectedService ? selectedService.nameEn || selectedService.name : translations[language].calendar.walkin.footer.noService}
						</span>
						<span>{getTotalDurationMinutes()} min</span>
						<span>{typeof selectedService?.price === "number" ? `$${selectedService.price}` : ""}</span>
					</div>
					<button
						className="sf-book-btn sf-book-btn--brand"
						onClick={handleConfirm}
						disabled={saving || !selectedStaff || !selectedService || !selectedTime}
					>
						{saving ? translations[language].calendar.walkin.footer.saving : translations[language].calendar.walkin.footer.confirm}
					</button>
				</div>

				{toast && <div className="sf-book-toast">{toast}</div>}
			</div>
		</div>
	);
};

export default WalkinModal;
